import { Injectable } from '@angular/core';

export type BillingValidationStatus = 'ready' | 'pending' | 'failed' | 'not-configured';

export interface BillingValidationCheck {
    key: string;
    step: number;
    title: string;
    description: string;
    status: BillingValidationStatus;
}

export interface BillingValidationChecklistState {
    loading: boolean;
    eyebrow: string;
    title: string;
    subtitle: string;
    contextLabel?: string;
    contextValue?: string;
    message?: string;
    ready: boolean;
    checks: BillingValidationCheck[];
}

export interface BillingValidationContext {
    documentLabel: string;
    meteringPointName?: string;
    billingCycleLabel?: string;
    tariffLinked: boolean | null | undefined;
    billingCycleSynced: boolean | null | undefined;
    billableDeviceLinked: boolean | null | undefined;
}

@Injectable({
    providedIn: 'root',
})
export class BillingValidationService {
    buildLoadingState(context: Pick<BillingValidationContext, 'documentLabel' | 'meteringPointName' | 'billingCycleLabel'>): BillingValidationChecklistState {
        return {
            loading: true,
            eyebrow: 'Billing checklist',
            title: 'Almost there',
            subtitle: `Checking the prerequisites for ${context.documentLabel}.`,
            contextLabel: 'Metering point',
            contextValue: context.meteringPointName?.trim() || 'N/A',
            ready: false,
            checks: [],
            message: '',
        };
    }

    buildErrorState(
        context: Pick<BillingValidationContext, 'documentLabel' | 'meteringPointName' | 'billingCycleLabel'>,
        message: string
    ): BillingValidationChecklistState {
        return {
            loading: false,
            eyebrow: 'Billing checklist',
            title: 'Almost there',
            subtitle: `Checking the prerequisites for ${context.documentLabel}.`,
            contextLabel: 'Metering point',
            contextValue: context.meteringPointName?.trim() || 'N/A',
            ready: false,
            checks: [
                {
                    key: 'tariff',
                    step: 1,
                    title: 'Tariff is linked to the metering point',
                    description: 'Verify that the selected metering point has at least one linked tariff.',
                    status: 'pending',
                },
                {
                    key: 'billing-cycle',
                    step: 2,
                    title: 'Billing cycle for the selected period has been synced',
                    description: 'Verify that billing data exists and the selected billing cycle has been successfully synced.',
                    status: 'pending',
                },
                {
                    key: 'device',
                    step: 3,
                    title: 'Linked device is billable',
                    description: 'Verify that the associated device is billable.',
                    status: 'pending',
                },
            ],
            message,
        };
    }

    buildChecklistState(context: BillingValidationContext): BillingValidationChecklistState {
        const checks: BillingValidationCheck[] = [
            {
                key: 'tariff',
                step: 1,
                title: 'Tariff is linked to the metering point',
                description: 'Verify that the selected metering point has at least one linked tariff.',
                status: this.resolveStatus(context.tariffLinked, 'not-configured'),
            },
            {
                key: 'billing-cycle',
                step: 2,
                title: 'Billing cycle for the selected period has been synced',
                description: 'Verify that billing data exists and the selected billing cycle has been successfully synced.',
                status: this.resolveStatus(context.billingCycleSynced, 'failed'),
            },
            {
                key: 'device',
                step: 3,
                title: 'Linked device is billable',
                description: 'Verify that the associated device is billable.',
                status: this.resolveStatus(context.billableDeviceLinked, 'not-configured'),
            },
        ];

        const ready = checks.every((check) => check.status === 'ready');
        const failures = checks.filter((check) => check.status !== 'ready' && check.status !== 'pending');

        return {
            loading: false,
            eyebrow: 'Billing checklist',
            title: 'Almost there',
            subtitle: `Checking the prerequisites for ${context.documentLabel}.`,
            contextLabel: 'Metering point',
            contextValue: context.meteringPointName?.trim() || 'N/A',
            ready,
            checks,
            message: ready ? this.buildReadyMessage(context.documentLabel) : this.buildFailureMessage(failures),
        };
    }

    private resolveStatus(value: boolean | null | undefined, failureStatus: Exclude<BillingValidationStatus, 'ready' | 'pending'>): BillingValidationStatus {
        if (value === true) {
            return 'ready';
        }

        if (value === false) {
            return failureStatus;
        }

        return 'pending';
    }

    private buildReadyMessage(documentLabel: string): string {
        return `${documentLabel} is ready to load.`;
    }

    private buildFailureMessage(failures: BillingValidationCheck[]): string {
        const messages = failures.map((check) => {
            switch (check.key) {
                case 'tariff':
                    return 'No tariff linked to this metering point.';
                case 'billing-cycle':
                    return 'Billing cycle for the selected period has not been synced.';
                case 'device':
                    return 'Linked device is not billable.';
                default:
                    return `${check.title} could not be verified.`;
            }
        });

        return messages.join(' ');
    }
}
